import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DocsToc, EndpointReference, SpecDownload } from "../components";
import { DocsTocItem } from "../interfaces";
import {
  DOCS_ENDPOINT_GROUP_ORDER,
  API_BASE_URL,
  getEndpointsByGroup,
} from "../utils";

// YOSHI-10464: the enumerations GET /v1/enums publishes, in the order the response carries them.
// They are listed by name with what each one resolves, because a catalog an integrator cannot see
// the shape of is a catalog it keeps hardcoding.
const ENUM_CATALOG_NAMES: string[] = [
  "sale_status",
  "sale_type",
  "event_status",
  "app_order_type",
  "payment_types",
];

// YOSHI-10464: the index of this page carries the five groups and, nested under each one, its
// operations: it is the longest page of the portal and the reader has to know where they are.
const TOC_ITEMS: DocsTocItem[] = DOCS_ENDPOINT_GROUP_ORDER.flatMap(
  (group): DocsTocItem[] => {
    const groupItem: DocsTocItem = {
      id: `group-${group}`,
      translationKey: `docs.endpoints.groups.${group}.title`,
      level: 1,
    };
    const endpointItems: DocsTocItem[] = getEndpointsByGroup(group).map(
      (endpoint): DocsTocItem => {
        return {
          id: endpoint.id,
          translationKey: `docs.operations.${endpoint.id}.title`,
          level: 2,
        };
      }
    );

    return [groupItem, ...endpointItems];
  }
);

export const Endpoints = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col-reverse gap-8 lg:flex-row lg:gap-12">
      <div className="min-w-0 flex-1 space-y-12">
        <div className="space-y-4">
          <h2 className="text-xl font-bold lg:text-2xl">
            {t("docs.endpoints.title")}
          </h2>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.endpoints.intro")}
          </p>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.endpoints.baseUrlNote")}{" "}
            <code className="break-all font-mono text-sm text-primary">
              {API_BASE_URL}
            </code>
          </p>
          {/* YOSHI-10464: the response examples are plain code, so the meaning of a field lives in
              the glossary and is pointed at from here once instead of repeated per operation. */}
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.endpoints.glossaryNote")}{" "}
            <Link
              to="/docs/glossary"
              className="font-semibold text-primary hover:underline"
            >
              {t("docs.nav.glossary")}
            </Link>
          </p>
        </div>

        <SpecDownload />

        {DOCS_ENDPOINT_GROUP_ORDER.map((group) => (
          <div key={group} className="space-y-8">
            <div className="space-y-3">
              <h3
                id={`group-${group}`}
                className="scroll-mt-6 text-lg font-bold lg:text-xl"
              >
                {t(`docs.endpoints.groups.${group}.title`)}
              </h3>
              <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
                {t(`docs.endpoints.groups.${group}.content`)}
              </p>
              {/* YOSHI-10464: the catalogs group is the only one whose value is the list itself, so
                  it states which enumerations travel and what each one resolves. */}
              {group === "catalogs" && (
                <>
                  <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
                    {t("docs.endpoints.groups.catalogs.usage")}
                  </p>
                  <div className="w-full overflow-x-auto">
                    <table className="w-full min-w-[26rem] border-collapse text-left text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th
                            scope="col"
                            className="py-2 pr-4 font-semibold text-gray-700"
                          >
                            {t("docs.common.catalog")}
                          </th>
                          <th
                            scope="col"
                            className="py-2 font-semibold text-gray-700"
                          >
                            {t("docs.common.description")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {ENUM_CATALOG_NAMES.map((catalog) => (
                          <tr
                            key={catalog}
                            className="border-b border-gray-100"
                          >
                            <td className="py-3 pr-4 align-top">
                              <code className="font-mono text-xs text-primary sm:text-sm">
                                {catalog}
                              </code>
                            </td>
                            <td className="py-3 leading-relaxed text-gray-500">
                              {t(
                                `docs.endpoints.groups.catalogs.items.${catalog}`
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            {getEndpointsByGroup(group).map((endpoint) => (
              <EndpointReference key={endpoint.id} endpoint={endpoint} />
            ))}
          </div>
        ))}
      </div>
      <DocsToc items={TOC_ITEMS} />
    </div>
  );
};
